import { useCallback, useEffect, useRef } from "react";
import { conversationFrameExpandedGeometry } from "@/lib/conversation-frame-expansion";
import { CONVERSATION_FRAME_WIDTH } from "@/lib/conversation-frame-surface";
import { focusSurfaceViewport } from "@/lib/focus/focus-surface";
import { getFrameDefaultSize } from "@/lib/frame-defaults";
import { applyFramePatch } from "@/lib/frame-ingest";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import {
  isFocusPresentationFrame,
  sameGeometry,
} from "./conversation-frame-utils";

const CONVERSATION_MIN_FRAME_WIDTH = getFrameDefaultSize("note").w;
const CONVERSATION_EXPANSION_EDGE_INSET = 16;
type ExpansionGeometry = { x: number; width: number } | null;

/**
 * Widens a canvas conversation frame while its compact composer expands and
 * restores the frame's own geometry afterwards. A frame on an independent
 * surface (detached or windowed) keeps its geometry on unmount.
 */
export function useConversationFrameInputExpansion(
  id: string,
  independentSurface: boolean,
): (deltaPx: number) => void {
  const expansionSnapshotRef = useRef<ExpansionGeometry>(null);
  const lastExpansionGeometryRef = useRef<ExpansionGeometry>(null);
  const suppressionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const clearSuppressionLater = useCallback(() => {
    if (suppressionTimerRef.current) {
      clearTimeout(suppressionTimerRef.current);
    }
    suppressionTimerRef.current = setTimeout(() => {
      suppressionTimerRef.current = null;
      useCanvasStore.getState().clearPresentationDimsSuppressed(id);
    }, 300);
  }, [id]);
  const clearInputExpansionSnapshot = useCallback(
    (clearSuppressionNow = true) => {
      expansionSnapshotRef.current = null;
      lastExpansionGeometryRef.current = null;
      if (!clearSuppressionNow) {
        clearSuppressionLater();
        return;
      }
      if (suppressionTimerRef.current) {
        clearTimeout(suppressionTimerRef.current);
        suppressionTimerRef.current = null;
      }
      useCanvasStore.getState().clearPresentationDimsSuppressed(id);
    },
    [clearSuppressionLater, id],
  );
  const clearInputExpansionIfGeometryChanged = useCallback(() => {
    const snapshot = expansionSnapshotRef.current;
    const lastExpansionGeometry = lastExpansionGeometryRef.current;
    if (!snapshot || !lastExpansionGeometry) return;
    const node = useCanvasStore.getState().nodes.find((n) => n.id === id);
    if (!node) {
      clearInputExpansionSnapshot(true);
      return;
    }
    const currentGeometry = {
      x: node.position.x,
      width: frameNodeSize(node, { width: CONVERSATION_FRAME_WIDTH }).width,
    };
    if (!sameGeometry(currentGeometry, lastExpansionGeometry)) {
      clearInputExpansionSnapshot(true);
    }
  }, [clearInputExpansionSnapshot, id]);
  useEffect(() => {
    return useCanvasStore.subscribe(clearInputExpansionIfGeometryChanged);
  }, [clearInputExpansionIfGeometryChanged]);

  const restoreInputExpansion = useCallback(
    (clearSuppressionNow = false) => {
      const snapshot = expansionSnapshotRef.current;
      if (!snapshot) {
        if (clearSuppressionNow) {
          if (suppressionTimerRef.current) {
            clearTimeout(suppressionTimerRef.current);
            suppressionTimerRef.current = null;
          }
          useCanvasStore.getState().clearPresentationDimsSuppressed(id);
        }
        return;
      }
      const lastExpansionGeometry = lastExpansionGeometryRef.current;
      const liveNode = useCanvasStore.getState().nodes.find((n) => n.id === id);
      const liveGeometry =
        liveNode && lastExpansionGeometry
          ? {
              x: liveNode.position.x,
              width: frameNodeSize(liveNode, {
                width: CONVERSATION_FRAME_WIDTH,
              }).width,
            }
          : null;
      if (
        liveGeometry &&
        lastExpansionGeometry &&
        !sameGeometry(liveGeometry, lastExpansionGeometry)
      ) {
        clearInputExpansionSnapshot(clearSuppressionNow);
        return;
      }
      expansionSnapshotRef.current = null;
      lastExpansionGeometryRef.current = null;
      const updates = {
        width: Math.max(snapshot.width, CONVERSATION_MIN_FRAME_WIDTH),
        ...(isFocusPresentationFrame(id) ? {} : { position_x: snapshot.x }),
      };
      applyFramePatch(id, updates);
      if (clearSuppressionNow) {
        if (suppressionTimerRef.current) {
          clearTimeout(suppressionTimerRef.current);
          suppressionTimerRef.current = null;
        }
        useCanvasStore.getState().clearPresentationDimsSuppressed(id);
      } else {
        clearSuppressionLater();
      }
    },
    [clearInputExpansionSnapshot, clearSuppressionLater, id],
  );

  const handleCompactExpansionDeltaChange = useCallback(
    (deltaPx: number) => {
      if (deltaPx <= 0) {
        restoreInputExpansion();
        return;
      }

      const state = useCanvasStore.getState();
      const node = state.nodes.find((n) => n.id === id);
      if (!node) return;
      const currentWidth = frameNodeSize(node, {
        width: CONVERSATION_FRAME_WIDTH,
      }).width;
      const targetWidth = Math.max(
        CONVERSATION_MIN_FRAME_WIDTH,
        CONVERSATION_FRAME_WIDTH + deltaPx,
      );
      const snapshotWidth = expansionSnapshotRef.current?.width ?? currentWidth;
      if (targetWidth <= snapshotWidth) {
        if (expansionSnapshotRef.current) restoreInputExpansion();
        return;
      }
      if (!expansionSnapshotRef.current) {
        if (suppressionTimerRef.current) {
          clearTimeout(suppressionTimerRef.current);
          suppressionTimerRef.current = null;
        }
        state.addPresentationDimsSuppressed(id);
        expansionSnapshotRef.current = {
          x: node.position.x,
          width: currentWidth,
        };
      }

      const snapshot = expansionSnapshotRef.current;
      const geometry = conversationFrameExpandedGeometry({
        baseX: snapshot.x,
        baseWidth: snapshot.width,
        delta: deltaPx,
        edgeInset: CONVERSATION_EXPANSION_EDGE_INSET,
        expansionBaseWidth: CONVERSATION_FRAME_WIDTH,
        minWidth: CONVERSATION_MIN_FRAME_WIDTH,
        transform: workspaceViewAdapter().transform(),
        surfaceWidth: focusSurfaceViewport().viewportWidth,
      });
      const nextGeometry = isFocusPresentationFrame(id)
        ? { x: snapshot.x, width: geometry.width }
        : geometry;
      lastExpansionGeometryRef.current = nextGeometry;
      applyFramePatch(id, {
        ...(isFocusPresentationFrame(id) ? {} : { position_x: geometry.x }),
        width: nextGeometry.width,
      });
    },
    [id, restoreInputExpansion],
  );

  useEffect(
    () => () => {
      if (!independentSurface) restoreInputExpansion(true);
    },
    [independentSurface, restoreInputExpansion],
  );
  return handleCompactExpansionDeltaChange;
}
